import * as React from 'react';
import { NavLink } from 'react-router-dom';
import { cn } from '../../lib/utils';

export interface SettingsDestination {
  key: string;
  label: string;
  href: string;
}

/**
 * One rail listing every settings destination.
 *
 * Account settings and storefront settings were two separate pages, each with
 * its own row of segmented tabs, both reachable only from the account menu — so
 * "where do I change my payout account" had no answer you could see. A single
 * rail puts every destination in one list, and the current one is marked rather
 * than the page being re-entered from a menu.
 *
 * Rendered as a nav of links, not buttons: these are destinations, so they
 * should be openable in a new tab and announced as navigation.
 */
export const SettingsRail: React.FC<{
  destinations: SettingsDestination[];
  className?: string;
}> = ({ destinations, className }) => (
  <nav aria-label="Settings" className={cn('space-y-0.5', className)}>
    {destinations.map((destination) => (
      <NavLink
        key={destination.key}
        to={destination.href}
        data-testid={`settings-nav-${destination.key}`}
        className={({ isActive }) =>
          cn(
            'block rounded-md px-3 py-2 text-sm transition-colors',
            isActive
              ? 'bg-primary/10 font-semibold text-primary'
              : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
          )
        }
      >
        {destination.label}
      </NavLink>
    ))}
  </nav>
);
